import { AuiIf, ThreadPrimitive } from '@assistant-ui/react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, Fab, IconButton, Typography, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChatDrawer } from '../hooks/use-chat-drawer';
import { useResizableDrawer } from '../hooks/use-resizable-drawer';

import { ComposerArea } from './composer-area';
import { MessageRow } from './message-row';
import { SidebarToggleButton, ThreadSidebar } from './thread-sidebar';
import { ToolRegistration } from './tools/tool-registry';
import { WelcomeMessage } from './welcome-message';

import type React from 'react';

interface DrawerResizeHandleProps {
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  isResizing: boolean;
}

const DrawerResizeHandle = ({
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: DrawerResizeHandleProps) => (
  <Box
    onPointerDown={onPointerDown}
    onPointerMove={onPointerMove}
    onPointerUp={onPointerUp}
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '8px',
      height: '100%',
      cursor: 'col-resize',
      zIndex: 1,
      touchAction: 'none',
      userSelect: 'none',
    }}
    aria-hidden="true"
  />
);

export const AiChatDrawer = () => {
  const { t } = useTranslation();
  const { isOpen, closeDrawer } = useChatDrawer();
  const { drawerWidth, isResizing, handlePointerDown, handlePointerMove, handlePointerUp } =
    useResizableDrawer(isOpen);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') closeDrawer();
  };

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={isOpen}
      sx={{
        width: 'var(--chat-drawer-width, 1000px)',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 'var(--chat-drawer-width, 1000px)',
          boxSizing: 'border-box',
          top: '64px',
          height: 'calc(100% - 64px)',
        },
      }}
      slotProps={{
        paper: {
          style: { width: drawerWidth } as React.CSSProperties,
        },
      }}
    >
      <DrawerResizeHandle
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        isResizing={isResizing}
      />
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        width="100%"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        sx={{ outline: 'none' }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          px={2}
          sx={{ height: 56, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          {sidebarCollapsed && (
            <SidebarToggleButton
              onToggle={() => {
                setSidebarCollapsed(false);
              }}
            />
          )}
          <Typography variant="h6" sx={{ flex: 1, ml: sidebarCollapsed ? 1 : 0 }}>
            {t('aiChat.title', 'AI Chat')}
          </Typography>
          <IconButton onClick={closeDrawer} aria-label={t('aiChat.drawer.close')} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body: sidebar + conversation */}
        <Box display="flex" flex={1} overflow="hidden">
          <ThreadSidebar
            collapsed={sidebarCollapsed}
            onToggle={() => {
              setSidebarCollapsed((prev) => !prev);
            }}
          />

          {/* Conversation area */}
          <ThreadPrimitive.Root style={{ width: '100%' }}>
            <ToolRegistration />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Scrollable viewport — ThreadPrimitive.Viewport IS the scroll container */}
              <ThreadPrimitive.Viewport
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: 16,
                  width: '100%',
                }}
              >
                {/* Empty state */}
                <AuiIf condition={(s) => s.thread.isEmpty && !s.thread.isLoading}>
                  <WelcomeMessage />
                </AuiIf>

                {/* loading state */}
                <AuiIf condition={(s) => s.thread.isLoading}>
                  <CircularProgress size={16} />
                </AuiIf>

                {/* Messages */}
                <ThreadPrimitive.Messages>{() => <MessageRow />}</ThreadPrimitive.Messages>
              </ThreadPrimitive.Viewport>

              {/* Scroll to bottom button */}
              <ThreadPrimitive.ScrollToBottom>
                <Fab
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 80,
                    right: 16,
                    zIndex: 1,
                  }}
                >
                  <ArrowDownwardIcon />
                </Fab>
              </ThreadPrimitive.ScrollToBottom>

              {/* Composer */}
              <ComposerArea />
            </Box>
          </ThreadPrimitive.Root>
        </Box>
      </Box>
    </Drawer>
  );
};
